import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createBooking, getAvailability, listTutors } from "../lib/api";
import { formatBookingDateEs } from "../lib/format";
import { toast } from "sonner";

const TIME_SLOTS = ["09:00", "11:00", "13:00", "16:00", "18:00", "20:00"];
const MINIMUM_NOTICE_MS = 12 * 60 * 60 * 1000;

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = () => toDateInput(new Date());

const availableSlotsForDate = (dateString) => TIME_SLOTS.filter((slot) => {
  const slotTime = new Date(`${dateString}T${slot}:00`).getTime();
  return slotTime >= Date.now() + MINIMUM_NOTICE_MS;
});

const firstAvailableDate = () => {
  const date = new Date();
  for (let offset = 0; offset < 14; offset += 1) {
    const candidate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + offset);
    const candidateString = toDateInput(candidate);
    if (availableSlotsForDate(candidateString).length > 0) {
      return candidateString;
    }
  }
  return today();
};

const availableDateOptions = () => {
  const start = new Date();
  return Array.from({ length: 14 }, (_, offset) => {
    const candidate = new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset);
    return toDateInput(candidate);
  }).filter((dateString) => availableSlotsForDate(dateString).length > 0);
};

const initialDate = firstAvailableDate();

export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [tutors, setTutors] = useState([]);
  const [availability, setAvailability] = useState({ payment_available: false });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    student_name: "",
    student_email: "",
    tutor_id: "",
    booking_date: initialDate,
    booking_time: availableSlotsForDate(initialDate)[0] || "",
  });
  const loadBookingOptions = useCallback(() => {
    Promise.all([listTutors(), getAvailability()])
      .then(([tutorList, settings]) => {
        setTutors(tutorList);
        setAvailability(settings);
        if (tutorList[0]) {
          setForm((current) => ({ ...current, tutor_id: tutorList[0].id }));
        }
      })
      .catch(() => toast.error("No se ha podido preparar la reserva."));
  }, []);

  useEffect(() => {
    loadBookingOptions();
  }, [loadBookingOptions]);

  const selectedTutor = useMemo(
    () => tutors.find((tutor) => tutor.id === form.tutor_id),
    [form.tutor_id, tutors],
  );

  const availableSlots = useMemo(
    () => availableSlotsForDate(form.booking_date),
    [form.booking_date],
  );

  const dateOptions = useMemo(() => availableDateOptions(), []);

  useEffect(() => {
    if (!availableSlots.includes(form.booking_time)) {
      setForm((current) => ({ ...current, booking_time: availableSlots[0] || "" }));
    }
  }, [availableSlots, form.booking_time]);

  const goToPayment = async () => {
    if (!selectedTutor) {
      toast.error("Elige un tutor para continuar.");
      return;
    }
    setBusy(true);
    try {
      const booking = await createBooking({
        ...form,
        tutor_display_name: selectedTutor.display_name,
      });
      localStorage.setItem("wespik.booking.id", booking.id);
      document.cookie = `wespik_booking_id=${booking.id}; Path=/; SameSite=Lax; Secure`;
      window.location.assign(booking.payment_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se ha podido guardar la reserva.");
      setBusy(false);
    }
  };

  const continueToSummary = () => {
    if (!form.student_name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.student_email)) {
      toast.error("Completa tu nombre y un email válido.");
      return;
    }
    if (!form.booking_time) {
      toast.error("Elige una fecha con huecos disponibles.");
      return;
    }
    setStep(2);
  };

  return (
    <main className="max-w-[960px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <div className="wk-eyebrow mb-5">Reserva</div>
      <h1
        className="text-[42px] lg:text-[56px] font-extrabold tracking-hero leading-[1.05]"
        style={{ color: "#1E1924" }}
        data-testid="booking-title"
      >
        Pack Prueba de Pronunciación
      </h1>
      <p className="mt-5 text-[16px] leading-[1.6]" style={{ color: "#70667A" }}>
        $22 · pago único · 2 sesiones de 30 min + 1 informe tras cada sesión.
      </p>

      <div className="mt-12 grid lg:grid-cols-[1.3fr_0.7fr] gap-10 items-start">
        <section className="wk-card p-7 lg:p-8" data-testid="booking-form-card">
          {step === 1 ? (
            <>
              <h2 className="text-[24px] font-bold tracking-hero" style={{ color: "#1E1924" }}>
                Elige el primer hueco
              </h2>
              <div className="mt-7 grid gap-5">
                <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
                  Nombre
                  <input
                    className="wk-input mt-1.5"
                    value={form.student_name}
                    maxLength={80}
                    onChange={(event) => setForm({ ...form, student_name: event.target.value })}
                    data-testid="booking-name-input"
                  />
                </label>
                <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
                  Email
                  <input
                    type="email"
                    className="wk-input mt-1.5"
                    value={form.student_email}
                    maxLength={120}
                    onChange={(event) => setForm({ ...form, student_email: event.target.value })}
                    data-testid="booking-email-input"
                  />
                </label>
                <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
                  Tutor
                  <select
                    className="wk-input mt-1.5"
                    value={form.tutor_id}
                    onChange={(event) => setForm({ ...form, tutor_id: event.target.value })}
                    data-testid="booking-tutor-select"
                  >
                    {tutors.map((tutor) => (
                      <option key={tutor.id} value={tutor.id}>
                        {tutor.display_name} · {tutor.specialty}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
                  Fecha de la primera sesión
                  <select
                    className="wk-input mt-1.5"
                    value={form.booking_date}
                    onChange={(event) => {
                      const bookingDate = event.target.value;
                      setForm({
                        ...form,
                        booking_date: bookingDate,
                        booking_time: availableSlotsForDate(bookingDate)[0] || "",
                      });
                    }}
                    data-testid="booking-date-input"
                  >
                    {dateOptions.map((dateString) => (
                      <option key={dateString} value={dateString}>
                        {formatBookingDateEs(dateString)}
                      </option>
                    ))}
                  </select>
                </label>
                <div>
                  <p className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
                    Hora
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        className={`wk-chip ${form.booking_time === slot ? "wk-chip-active" : ""}`}
                        onClick={() => setForm({ ...form, booking_time: slot })}
                        data-testid={`booking-time-${slot.replace(":", "-")}`}
                      >
                        {slot}
                      </button>
                    ))}
                    {availableSlots.length === 0 ? (
                      <p className="text-[13px]" style={{ color: "#70667A" }} data-testid="booking-no-slots">
                        Elige otra fecha para ver huecos con al menos 12 h de antelación.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="wk-btn wk-btn-primary mt-8"
                onClick={continueToSummary}
                data-testid="booking-continue-button"
              >
                Revisar reserva
              </button>
            </>
          ) : (
            <>
              <h2 className="text-[24px] font-bold tracking-hero" style={{ color: "#1E1924" }}>
                Revisa tu reserva
              </h2>
              <dl className="mt-7 space-y-4 text-[15px]" data-testid="booking-summary">
                <div className="flex justify-between gap-4">
                  <dt style={{ color: "#70667A" }}>Tutor</dt>
                  <dd className="font-semibold text-right" style={{ color: "#1E1924" }}>
                    {selectedTutor?.display_name}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt style={{ color: "#70667A" }}>Primera sesión</dt>
                  <dd className="font-semibold text-right" style={{ color: "#1E1924" }}>
                    {formatBookingDateEs(form.booking_date)} · {form.booking_time}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt style={{ color: "#70667A" }}>Producto</dt>
                  <dd className="font-semibold text-right" style={{ color: "#1E1924" }}>
                    Pack Prueba de Pronunciación
                  </dd>
                </div>
              </dl>
              <p className="mt-7 text-[13px] leading-[1.5]" style={{ color: "#70667A" }}>
                Al continuar, el hueco se retiene durante 30 minutos.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="wk-btn wk-btn-ghost"
                  onClick={() => setStep(1)}
                  data-testid="booking-back-button"
                >
                  Cambiar datos
                </button>
                <button
                  type="button"
                  className="wk-btn wk-btn-primary"
                  disabled={busy || !availability.payment_available}
                  onClick={goToPayment}
                  data-testid="booking-pay-button"
                >
                  {availability.payment_available ? "Pagar $22" : "Disponible muy pronto"}
                </button>
              </div>
            </>
          )}
        </section>

        <aside className="wk-card p-7" data-testid="booking-product-card">
          <div className="wk-eyebrow mb-4">Incluye</div>
          <ul className="space-y-4 text-[14px] leading-[1.5]" style={{ color: "#1E1924" }}>
            <li>2 sesiones de conversación de 30 min.</li>
            <li>1 informe de pronunciación tras cada sesión.</li>
            <li>Tu tutor elegido desde el primer hueco.</li>
          </ul>
          <div className="h-px my-6" style={{ background: "#DEDAE2" }} />
          <div className="font-mono-num text-[36px] font-extrabold" style={{ color: "#7228C8" }}>
            $22
          </div>
          <p className="mt-2 text-[12px]" style={{ color: "#70667A" }}>
            Pago único.
          </p>
        </aside>
      </div>
    </main>
  );
}